import React from 'react';

import {assert} from 'Asserts';
import {CAPABILITY_TO_NAME} from 'auth/capabilities/CapabilityFeatureConstants';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getProcedureRunUIString, StringCase} from 'procedures/ProceduresStringKeys';
import {
  WorksheetAnalyzeButtonProcedureMethodQuery,
  WorksheetAnalyzeButtonProcedureMethodQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode as AccessiblePMEI,
  WorksheetAnalyzeButtonProcedureMethodQueryVariables,
} from 'procedures/shared-components/analysis/__generated/WorksheetAnalyzeButtonProcedureMethodQuery';
import {
  WorksheetAnalyzeButtonQuery,
  WorksheetAnalyzeButtonQueryVariables,
} from 'procedures/shared-components/analysis/__generated/WorksheetAnalyzeButtonQuery';
import AnalyzeProcedureModal from 'procedures/shared-components/analysis/AnalyzeProcedureModal';
import {
  WORKSHEET_ANALYZE_BUTTON_PROCEDURE_METHOD_QUERY,
  WORKSHEET_ANALYZE_BUTTON_QUERY,
} from 'procedures/shared-components/analysis/WorksheetAnalyzeButtonQuery';
import {
  isFeatureEnabledForProcedureType,
  PROCEDURE_TYPE_TO_CAPABILITY,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {useProcedureWorksheetContext} from 'procedures/worksheets/ProcedureWorksheetContext';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

interface WorksheetAnalyzeButtonProps {
  className?: string;
  worksheetId: string;
  worksheetName: string;
  isWorksheetInReview: boolean;
  refetch: () => Promise<void>;
}

export const ANALYZE_BUTTON_TOOLTIP_TEXT = 'Analyze data from this worksheet';
export const LOADING_TOOLTIP_TEXT = 'Loading...';
export const DISABLED_BUTTON_TOOLTIP_TEXT = 'Analyzing data is disabled because the worksheet is in review.';
export const SINGLE_PMEI_ARCHIVED_TOOLTIP_TEXT =
  'Analyzing data is disabled because this unit operation is archived.';
export const ALL_PMEIS_ARCHIVED_TOOLTIP_TEXT =
  'Analyzing data is disabled because all condition replicates are archived.';
const ANALYZE_PROCEDURE_MODAL_KEY = 'Analyze procedure';

export const getNoCapabilityTooltipText = (procedureType: ProcedureType): string => {
  const procedureRunText = getProcedureRunUIString(procedureType, {
    prependAorAn: true,
    stringCase: StringCase.LOWER,
  });
  const applicationName = CAPABILITY_TO_NAME[PROCEDURE_TYPE_TO_CAPABILITY[procedureType]];
  return `Analyzing ${procedureRunText} worksheet requires access to the ${applicationName} application. Contact your administrator for access.`;
};

export function getDisabledTooltipText(
  isWorksheetInReview: boolean,
  pmei: AccessiblePMEI | null,
  areAllPmeisArchived: boolean,
  pmeiCount: number
): string | null {
  if (isWorksheetInReview) {
    return DISABLED_BUTTON_TOOLTIP_TEXT;
  }
  if (areAllPmeisArchived) {
    return pmeiCount === 1 ? SINGLE_PMEI_ARCHIVED_TOOLTIP_TEXT : ALL_PMEIS_ARCHIVED_TOOLTIP_TEXT;
  }
  if (pmei == null) {
    return LOADING_TOOLTIP_TEXT;
  }
  if (!isFeatureEnabledForProcedureType(pmei.procedureRun.procedureType, ProceduresFeature.CREATE_ANALYSIS)) {
    return getNoCapabilityTooltipText(pmei.procedureRun.procedureType);
  }
  return null;
}

export default function WorksheetAnalyzeButton(props: WorksheetAnalyzeButtonProps): JSX.Element {
  const {PMEIs} = useProcedureWorksheetContext();
  const accessiblePMEIFromContext = PMEIs.length > 0 && !isInaccessible(PMEIs[0]) ? PMEIs[0] : null;
  const areAllPmeisArchived =
    PMEIs.length > 0 && PMEIs.every((pmei) => !isInaccessible(pmei) && pmei.archiveData.archived);
  // The PMEIs we get from context only have the method task info, so we need to fetch additional fields
  const procedureMethodQueryResult = useSkippableBenchlingQuery<
    WorksheetAnalyzeButtonProcedureMethodQuery,
    WorksheetAnalyzeButtonProcedureMethodQueryVariables
  >(WORKSHEET_ANALYZE_BUTTON_PROCEDURE_METHOD_QUERY, {
    // We just fetch the first PMEI, since we're only fetching the procedure run and the analysis template
    // configurations, which are expected to be the same for all PMEIs being executed in the worksheet
    variables: {
      methodExecutionInstanceId: accessiblePMEIFromContext?.id ?? '',
    },
    skip: accessiblePMEIFromContext == null,
  });
  const pmei =
    procedureMethodQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(procedureMethodQueryResult.data.procedureMethodExecutionInstance)
      ? procedureMethodQueryResult.data.procedureMethodExecutionInstance
      : null;

  const onClick = async (): Promise<void> => {
    const {worksheet} = await doQuerySafe<WorksheetAnalyzeButtonQuery, WorksheetAnalyzeButtonQueryVariables>(
      {query: WORKSHEET_ANALYZE_BUTTON_QUERY},
      {variables: {id: props.worksheetId}}
    );

    if (isInaccessible(worksheet)) {
      assert(false, 'Expected worksheet to be accessible');
    } else {
      LegacyModalContainer.open(
        ANALYZE_PROCEDURE_MODAL_KEY,
        <AnalyzeProcedureModal
          onRequestClose={() => {
            LegacyModalContainer.close(ANALYZE_PROCEDURE_MODAL_KEY);
          }}
          onRequestSubmit={props.refetch}
          worksheet={worksheet}
          pmei={pmei}
        />
      );
    }
  };

  const disabledTooltipText = getDisabledTooltipText(
    props.isWorksheetInReview,
    pmei,
    areAllPmeisArchived,
    PMEIs.length
  );

  return (
    <Tooltip placement={FloatingPlacement.TOP} text={disabledTooltipText ?? ANALYZE_BUTTON_TOOLTIP_TEXT}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={props.className}
        icon="analytics_analysis"
        onClick={onClick}
        text="Analyze"
        size={ButtonSize.MEDIUM}
        dataTestAttrs={{component: 'WorksheetAnalyzeButton', element: 'root'}}
        disabled={disabledTooltipText != null}
      />
    </Tooltip>
  );
}
